import {
  SpecialtyKeywordConfigType,
  SpecialtyKeywordRow
} from './SpecialtyKeywordTable';
import { Controller, get, useFormContext } from 'react-hook-form';
import { ButtonIcon, Checkbox, Table } from '@morressier/alembic';
import { CustomSearchSelect } from './CustomSearchSelect';
import { Box, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
  fetchReviewerExpertiseSuggestionKeywords,
  fetchReviewerExpertiseSuggestionSpecialty
} from 'src/api/signUpMutation';
import { FormFieldSelect as IFormFieldSelect } from '@publishing-engine/domain/types/article';
import { useAuthRealm } from 'src/routes/AuthRealmAccessControl/AuthRealmContext';

export const getConfigProperties = (config: IFormFieldSelect) => {
  const {
    name,
    caseDataFields: { main: field },
    params: { validations, labels }
  } = config;

  return {
    name,
    field,
    validations,
    labels
  };
};

const formatLabelText = (value?: string) =>
  (value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const SpecialtyKeywordTableRow = ({
  rowField,
  fieldName,
  index,
  handleAddRow,
  selected,
  toggleRow,
  handleDeleteRow,
  handleNewKeywordClick,
  handleCellChange,
  isKeywordOptionDisabled,
  configs,
  isMaxRowLimit
}: {
  rowField: any;
  fieldName: string;
  index: number;
  handleAddRow: any;
  toggleRow: any;
  selected: any;
  handleDeleteRow: any;
  handleNewKeywordClick: any;
  handleCellChange: any;
  isKeywordOptionDisabled: (rowIndex: number, keywordName?: string) => boolean;
  configs: SpecialtyKeywordConfigType;
  isMaxRowLimit: boolean;
}) => {
  const specialtyConfig = getConfigProperties(configs.Specialty);
  const subSpecialtyConfig = configs.SubSpecialty
    ? getConfigProperties(configs.SubSpecialty)
    : null;

  const keywordConfig = getConfigProperties(configs.Keyword);
  const scandinavianKeywordConfig = configs.ScandinavianKeyword
    ? getConfigProperties(configs.ScandinavianKeyword)
    : null;

  const { authRealm } = useAuthRealm();

  const {
    watch,
    formState: { errors },
    control
  } = useFormContext();

  const specialtyPath = `${fieldName}.${index}.specialty`;
  const subSpecialtyPath = `${fieldName}.${index}.subSpecialty`;
  const keywordPath = `${fieldName}.${index}.keyword`;
  const scandinavianKeywordPath = `${fieldName}.${index}.scandinavianKeyword`;

  const specialty = watch(specialtyPath) as SpecialtyKeywordRow['specialty'];
  const subSpecialty = watch(
    subSpecialtyPath
  ) as SpecialtyKeywordRow['subSpecialty'];
  const keyword = watch(keywordPath) as SpecialtyKeywordRow['keyword'];
  const scandinavianKeyword = watch(
    scandinavianKeywordPath
  ) as SpecialtyKeywordRow['scandinavianKeyword'];

  const specialtyError = get(errors, specialtyPath);
  const subSpecialtyError = get(errors, subSpecialtyPath);
  const keywordError = get(errors, keywordPath);
  const scandinavianKeywordError = get(errors, scandinavianKeywordPath);

  return (
    <Table.Row
      key={rowField.id}
      alignItems="start"
      _last={{ borderBottom: 'none' }}
      data-cy="table-row"
      display="flex"
      gap={4}
      minWidth="100%"
      width="100%"
    >
      <Table.RowCell>
        <Checkbox
          ariaLabel={`Select row ${index + 1}`}
          id={`row-${index}`}
          name={`row-${index}`}
          isChecked={selected[index] || false}
          onChange={() => toggleRow(index)}
        >
          <Box as="span" srOnly>
            Select row {index + 1}
          </Box>
        </Checkbox>
      </Table.RowCell>

      <Table.RowCell flex={1}>
        <Controller
          control={control}
          name={specialtyPath}
          rules={{
            required: `${specialtyConfig.name} is required`
          }}
          render={() => (
            <CustomSearchSelect
              value={specialty || ''}
              onChange={handleCellChange}
              placeholder="specialty"
              isInvalid={!!specialtyError}
              searchMethod={fetchReviewerExpertiseSuggestionSpecialty}
              fieldName={specialtyPath}
              errorMessage={specialtyError?.message as string}
              rowIndex={index}
              handleNewKeywordClick={handleNewKeywordClick}
              showNewKeyword={authRealm === 'scup'|| authRealm === 'scup'}
              newKeywordFieldName="specialty"
              newKeywordLabel="specialty"
            />
          )}
        />
      </Table.RowCell>

      {subSpecialtyConfig && (
        <Table.RowCell flex={1}>
          <Controller
            control={control}
            name={subSpecialtyPath}
            rules={{
              required: subSpecialtyConfig.validations?.required
                ? `${subSpecialtyConfig.name} is required`
                : false
            }}
            render={() => (
              <CustomSearchSelect
                value={subSpecialty || ''}
                onChange={handleCellChange}
                placeholder="sub-specialty"
                isInvalid={!!subSpecialtyError}
                errorMessage={subSpecialtyError?.message as string}
                searchMethod={fetchReviewerExpertiseSuggestionKeywords}
                fieldName={subSpecialtyPath}
                termSearch={specialty?.termId}
                isDisabled={!specialty?.termId || false}
                rowIndex={index}
                handleNewKeywordClick={handleNewKeywordClick}
                newKeywordFieldName="subSpecialty"
                newKeywordLabel="sub-specialty"
              />
            )}
          />
        </Table.RowCell>
      )}

      <Table.RowCell flex={1}>
        <Controller
          control={control}
          name={keywordPath}
          rules={{
            required: keywordConfig.validations?.required
              ? `${keywordConfig.name} is required`
              : false
          }}
          render={() => (
            <CustomSearchSelect
              value={keyword || ''}
              onChange={handleCellChange}
              placeholder="keyword"
              isInvalid={!!keywordError}
              errorMessage={keywordError?.message as string}
              searchMethod={fetchReviewerExpertiseSuggestionKeywords}
              fieldName={keywordPath}
              termSearch={
                subSpecialtyConfig ? subSpecialty?.termId : specialty?.termId
              }
              isDisabled={!specialty?.termId || false}
              rowIndex={index}
              handleNewKeywordClick={handleNewKeywordClick}
              newKeywordFieldName="keyword"
              newKeywordLabel="keyword"
              isOptionDisabled={(item: { name?: string }) =>
                isKeywordOptionDisabled(index, item?.name)
              }
            />
          )}
        />
      </Table.RowCell>

      {scandinavianKeywordConfig && (
        <Table.RowCell flex={1}>
          <Controller
          control={control}
          name={scandinavianKeywordPath}
          rules={{
            required: scandinavianKeywordConfig.validations?.required
              ? `${
                  formatLabelText(scandinavianKeywordConfig.name) ||
                  'Scandinavian Keyword'
                } is required`
              : false
          }}
            render={() => (
              <CustomSearchSelect
                value={scandinavianKeyword || ''}
                onChange={handleCellChange}
                placeholder="scandinavian keyword"
                isInvalid={!!scandinavianKeywordError}
                errorMessage={scandinavianKeywordError?.message as string}
                searchMethod={fetchReviewerExpertiseSuggestionKeywords}
                fieldName={scandinavianKeywordPath}
                termSearch={keyword?.termId}
                rowIndex={index}
                isDisabled={!specialty?.termId || false}
                handleNewKeywordClick={handleNewKeywordClick}
                newKeywordFieldName="scandinavianKeyword"
                newKeywordLabel="scandinavian keyword"
              />
            )}
          />
        </Table.RowCell>
      )}

      <Table.RowCell width={5}>
        <Tooltip label="Add row" placement="top" hasArrow>
          <Box>
            <ButtonIcon
              ariaLabel="Add row"
              icon={<FontAwesomeIcon icon={faPlus} />}
              data-cy="plus-icon"
              ml={1}
              onClick={handleAddRow}
              disabled={isMaxRowLimit}
            />
          </Box>
        </Tooltip>
      </Table.RowCell>

      <Table.RowCell width={10}>
        <Tooltip label="Delete row" placement="top" hasArrow>
          <Box>
            <ButtonIcon
              ariaLabel="Delete this row"
              icon={<FontAwesomeIcon icon={faTrashCan} />}
              data-cy={`delete-row-${index}`}
              ml={1}
              onClick={() => handleDeleteRow(index)}
            />
          </Box>
        </Tooltip>
      </Table.RowCell>
    </Table.Row>
  );
};

export default SpecialtyKeywordTableRow;
